import type { Database, Json } from './database.types';
import type { ContactUpsertInput } from './schemas';
import { systemNow } from './clock';

export type Contact = Database['public']['Tables']['contacts']['Row'];
export type Venue = Database['public']['Tables']['venues']['Row'];
export type ContactWithVenue = Contact & {
  venues?: Pick<Venue, 'google_places_id' | 'name'> | null;
};
export type ContactConsentStatus = Database['public']['Enums']['contact_consent_status'];
export type { ContactUpsertInput } from './schemas';

export type NormalizedContactInput = Readonly<{
  tenant_id: string;
  venue_id: string | null;
  user_id: string | null;
  name: string | null;
  email: string | null;
  phone: string | null;
  consent_status: ContactConsentStatus;
  consented_at: string | null;
  source: string;
  source_detail: Json;
}>;

export function normalizeEmail(email: string | null | undefined): string | null {
  const normalized = email?.trim().toLowerCase() ?? '';
  return normalized.length > 0 ? normalized : null;
}

export function normalizeOptionalText(value: string | null | undefined): string | null {
  const normalized = value?.trim() ?? '';
  return normalized.length > 0 ? normalized : null;
}

export function normalizePhone(phone: string | null | undefined): string | null {
  return normalizeOptionalText(phone);
}

export function normalizeConsentStatus(
  status: ContactConsentStatus | null | undefined
): ContactConsentStatus {
  return status ?? 'unknown';
}

export function normalizeContactInput(
  input: ContactUpsertInput,
  now = systemNow()
): NormalizedContactInput {
  const consentStatus = normalizeConsentStatus(input.consentStatus);
  return {
    tenant_id: input.tenantId,
    venue_id: normalizeOptionalText(input.venueId),
    user_id: normalizeOptionalText(input.userId),
    name: normalizeOptionalText(input.name),
    email: normalizeEmail(input.email),
    phone: normalizePhone(input.phone),
    consent_status: consentStatus,
    consented_at: consentStatus === 'opted_in' ? now.toISOString() : null,
    source: normalizeOptionalText(input.source) ?? 'unknown',
    source_detail: input.sourceDetail ?? {},
  };
}

export function shouldPromptForEventContact(
  contact: Pick<Contact, 'name' | 'email' | 'phone'> | null,
  fallbackEmail?: string | null
): boolean {
  return !(
    normalizeOptionalText(contact?.name) &&
    (normalizeEmail(contact?.email) || normalizeEmail(fallbackEmail)) &&
    normalizePhone(contact?.phone)
  );
}

export function mergeContactForUpsert(
  existing: Contact | null,
  input: NormalizedContactInput
): NormalizedContactInput {
  if (!existing) {
    return input;
  }

  const nextConsent =
    input.consent_status === 'unknown' ? existing.consent_status : input.consent_status;

  return {
    ...input,
    user_id: input.user_id ?? existing.user_id,
    venue_id: input.venue_id ?? existing.venue_id,
    name: input.name ?? existing.name,
    email: input.email ?? existing.email,
    phone: input.phone ?? existing.phone,
    consent_status: nextConsent,
    consented_at: nextConsent === 'opted_in' ? (existing.consented_at ?? input.consented_at) : null,
    source: input.source === 'unknown' ? existing.source : input.source,
    source_detail: input.source_detail,
  };
}

export function escapeCsvCell(value: string | number | null | undefined): string {
  const text = value == null ? '' : String(value);
  if (!/[",\n\r]/.test(text)) {
    return text;
  }
  return `"${text.replaceAll('"', '""')}"`;
}

export function contactsToCsv(contacts: ContactWithVenue[]): string {
  const headers = [
    'name',
    'email',
    'phone',
    'venue_name',
    'google_places_id',
    'consent_status',
    'consented_at',
    'source',
    'updated_at',
  ];
  const rows = contacts.map((contact) =>
    [
      contact.name,
      contact.email,
      contact.phone,
      contact.venues?.name,
      contact.venues?.google_places_id,
      contact.consent_status,
      contact.consented_at,
      contact.source,
      contact.updated_at,
    ]
      .map(escapeCsvCell)
      .join(',')
  );
  return [headers.join(','), ...rows].join('\n');
}
